import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Landmark, CreditCard, TrendingUp, PiggyBank, ShieldCheck, Wallet, Zap, Share2 } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { useAuth } from "@/context/AuthContext";
import { ShareCampaignDialog } from "@/components/ShareCampaignDialog";

const cleanName = (n = "") => n.replace(/\s*Campaign$/i, "").trim();

function catIcon(cat = "") {
  const c = cat.toLowerCase();
  if (c.includes("loan")) return Landmark;
  if (c.includes("card")) return CreditCard;
  if (c.includes("demat") || c.includes("mutual") || c.includes("invest")) return TrendingUp;
  if (c.includes("saving") || c.includes("account")) return Wallet;
  if (c.includes("insurance")) return ShieldCheck;
  if (c.includes("cibil") || c.includes("credit")) return PiggyBank;
  return Zap;
}

function payoutLabel(c) {
  if (c.payout_type === "percent" && c.payout_percent) return `Earn ${c.payout_percent}% per conversion`;
  const amt = Number(c.payout_pool || c.max_payout || 0);
  return amt > 0 ? `Earn up to ₹${amt.toLocaleString("en-IN")}` : "Earn on every conversion";
}

export default function Products() {
  useSEO({ title: "Live Campaigns — Apply & Earn", path: "/products", description: "Browse live Credit Kendra campaigns — demat accounts, loans, credit cards, savings & more. Apply directly or refer and earn rewards on every confirmed conversion.", keywords: "live campaigns, apply online, refer and earn, demat, personal loan, credit card, cibil loan India" });
  const [campaigns, setCampaigns] = useState([]);
  const [cat, setCat] = useState("all");
  const [imgErr, setImgErr] = useState({});
  const [shareCamp, setShareCamp] = useState(null);
  const { user } = useAuth();
  const origin = window.location.origin;

  useEffect(() => {
    api.get("/campaigns/public").then((r) => setCampaigns(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const cats = useMemo(() => {
    const s = new Set();
    campaigns.forEach((c) => c.category && s.add(c.category));
    return Array.from(s);
  }, [campaigns]);

  const shown = cat === "all" ? campaigns : campaigns.filter((c) => c.category === cat);

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-12 min-h-[70vh]">
      <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-[#0B223D]">Live Campaigns</h1>
      <p className="text-slate-500 mt-2">Apply directly or generate a referral link to earn on every confirmed conversion.</p>

      {cats.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8 mb-8">
          {["all", ...cats].map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`category-${c}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${cat === c ? "bg-[#0B223D] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#2563EB]/50"}`}>
              {c === "all" ? "All Campaigns" : c}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {shown.map((c) => {
          const Icon = catIcon(c.category);
          const img = c.desktop_creative || c.mobile_creative || "";
          return (
            <Link to={`/apply/${c.campaign_code}`} key={c.id} data-testid={`campaign-card-${c.campaign_code}`}
              className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-[#0B223D]/5 hover:-translate-y-1 hover:border-[#2563EB]/40 transition-all">
              <div className="h-44 flex items-center justify-center bg-gradient-to-br from-slate-50 to-[#EAF2FF] p-4 relative">
                {img && !imgErr[c.id] ? (
                  <img src={img} alt={cleanName(c.name)} loading="lazy" className="max-w-full max-h-full object-contain"
                    onError={() => setImgErr((s) => ({ ...s, [c.id]: true }))} />
                ) : (
                  <div className="flex flex-col items-center justify-center text-[#0B223D]">
                    <Icon className="w-12 h-12 text-[#2563EB]" />
                    <span className="mt-2 text-sm font-bold">{cleanName(c.name)}</span>
                  </div>
                )}
                <span className="absolute top-3 right-3 bg-[#0D9488]/10 text-[#0D9488] border border-[#0D9488]/20 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                  {payoutLabel(c)}
                </span>
              </div>
              <div className="p-5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-brand">{c.category || "Campaign"}</span>
                <h3 className="font-display font-bold text-lg text-[#0B223D] mt-1">{cleanName(c.name)}</h3>
                {c.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{c.description}</p>}
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="inline-flex text-[#2563EB] font-bold items-center gap-1 text-sm">View &amp; Apply <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
                  <button
                    onClick={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      const url = `${origin}/apply/${c.campaign_code}${user?.user_code ? `?user_id=${user.user_code}` : ""}`;
                      setShareCamp({ c, url });
                    }}
                    data-testid={`share-campaign-${c.campaign_code}`}
                    title={user ? "Share & Earn — aapke referral link ke saath" : "Share karein (referral credit ke liye login karein)"}
                    className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold px-3.5 py-2 rounded-full transition-colors shrink-0">
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      {campaigns.length > 0 && shown.length === 0 && <p className="text-slate-400 py-16 text-center">No live campaigns in this category.</p>}
      {campaigns.length === 0 && <p className="text-slate-400 py-16 text-center">No live campaigns right now. Please check back soon.</p>}

      {shareCamp && <ShareCampaignDialog campaign={shareCamp.c} url={shareCamp.url} user={user} onClose={() => setShareCamp(null)} />}
    </div>
  );
}
